/**
 * Sim seed for the Reporting page (data layer only): `?sim=1&reporting=<mode>`.
 *
 *   (absent)  → unchanged defaults: no earnings history, and distributions
 *               only when `?positions=` seeds them (three cycles, below the
 *               chart rule), so the page shows its empty state
 *   history   → 180 days of balances and weekly payouts across three
 *               positions (two on one surface, to exercise the short-address
 *               label), so every chart renders; 7d holds one or two weekly
 *               cycles, which exercises the in-range "too few points" note
 *   error     → the earnings and distributions requests fail, for the
 *               error state and its Retry
 *
 * The FE is identical in every mode; only what the GETs return changes.
 */

import type { ClientEarningsResponse, DistributionItem } from "../clientPositions";

import { MOCK_EARNINGS, MOCK_PROGRAM_SURFACES, MOCK_SAVED_DESTINATIONS } from "./mockData";

export type SimReportingMode = "default" | "history" | "error";

export function readSimReportingMode(): SimReportingMode {
    if (typeof window === "undefined") return "default";
    const raw = new URLSearchParams(window.location.search).get("reporting");
    return raw === "history" || raw === "error" ? raw : "default";
}

const DAY_MS = 24 * 60 * 60 * 1000;
const HISTORY_DAYS = 180;
/** Last day of history: the sim's fixed "today" minus one (snapshot lag). */
const HISTORY_END_MS = Date.parse("2026-06-07T00:00:00.000Z");
const PAYOUT_EVERY_DAYS = 7;
const SIM_APY = 0.068;

interface SimReportingPosition {
    uuid: string;
    surfaceIndex: number;
    wallet: string;
    startBalance: number;
    /** Day index (0 = first day of history) the position opens. */
    opensOn: number;
}

const POSITIONS: readonly SimReportingPosition[] = [
    {
        uuid: "position-sim-report-treasury",
        surfaceIndex: 0,
        wallet: "7Np41oeYqPefeNQEHSv1UDhYrehxin3NStELkfyBgPMd",
        startBalance: 1_250_000,
        opensOn: 0
    },
    {
        uuid: "position-sim-report-ops",
        surfaceIndex: 0,
        wallet: "E9Yffvreri1KbHhE84J7UQoPjphL4KLC1ZwfE8PXWi7x",
        startBalance: 480_000,
        opensOn: 70
    },
    {
        uuid: "position-sim-report-squads",
        surfaceIndex: 1,
        wallet: "Mu1tisigMockAddre55Examp1eForSimMode00000000001",
        startBalance: 640_000,
        opensOn: 115
    }
];

function balanceOn(position: SimReportingPosition, day: number): number {
    if (day < position.opensOn) return 0;
    // Slow growth plus a gentle wobble, so the balance line is not a ruler.
    const age = day - position.opensOn;
    return Math.round(position.startBalance * (1 + age * 0.0006 + Math.sin(age / 9) * 0.01));
}

function toCents(value: number): number {
    return Math.round(value * 100) / 100;
}

interface SimReportingHistory {
    earnings: ClientEarningsResponse;
    distributions: DistributionItem[];
}

let cached: SimReportingHistory | null = null;

/** Deterministic 180-day history; balances and payouts agree with each other. */
export function simReportingHistory(): SimReportingHistory {
    if (cached) return cached;
    const startMs = HISTORY_END_MS - (HISTORY_DAYS - 1) * DAY_MS;
    const distributions: DistributionItem[] = [];
    const daily: ClientEarningsResponse["daily"] = [];
    let cumulative = 0;

    for (let day = 0; day < HISTORY_DAYS; day++) {
        const ts = startMs + day * DAY_MS;
        const balances = POSITIONS.map((p) => balanceOn(p, day));
        const total = balances.reduce((sum, b) => sum + b, 0);

        let earnedToday = 0;
        if (day > 0 && day % PAYOUT_EVERY_DAYS === 0) {
            POSITIONS.forEach((position, index) => {
                // A week's accrual on the average balance across the week.
                let accrued = 0;
                for (let d = day - PAYOUT_EVERY_DAYS + 1; d <= day; d++) {
                    accrued += (balanceOn(position, d) * SIM_APY) / 365;
                }
                const amount = toCents(accrued * (1 + Math.sin(day / 17) * 0.04));
                if (amount <= 0) return;
                const surface = MOCK_PROGRAM_SURFACES[position.surfaceIndex % MOCK_PROGRAM_SURFACES.length];
                earnedToday += amount;
                distributions.push({
                    uuid: `dist-sim-report-${day}-${index}`,
                    // Claims for one cycle land seconds apart, as on chain.
                    distributionDate: new Date(ts + 14 * 60 * 60 * 1000 + index * 7000).toISOString(),
                    amountUsdv: amount,
                    positionUuid: position.uuid,
                    positionName: surface.name,
                    pool: surface.name,
                    sourceWallet: position.wallet,
                    destinationWallet: MOCK_SAVED_DESTINATIONS[index % MOCK_SAVED_DESTINATIONS.length].address,
                    txSignature: `SimReportTx${day}x${index}Sig11111111111111111111111111111111111111111111111111`
                });
            });
        }
        cumulative = toCents(cumulative + earnedToday);
        daily.push({
            timestampMs: ts,
            cumulativeEarnedUsdv: cumulative,
            apyPct: total > 0 ? Math.round((earnedToday / total) * 365 * 100 * 100) / 100 : 0,
            dailyBalanceUsdv: total
        });
    }

    const last = daily[daily.length - 1];
    cached = {
        earnings: {
            ...MOCK_EARNINGS,
            daily,
            headline: {
                ...MOCK_EARNINGS.headline,
                trailingApyPct: SIM_APY * 100,
                earnedWindowUsdv: last?.cumulativeEarnedUsdv ?? 0,
                earnedTotalUsdv: last?.cumulativeEarnedUsdv ?? 0
            },
            snapshotAt: new Date(HISTORY_END_MS + DAY_MS).toISOString()
        },
        distributions
    };
    return cached;
}

/** What the sim's reporting GETs throw in `?reporting=error`. */
export function simReportingFailure(): Error {
    return new Error("Simulated reporting outage");
}
